// autopilot-environment-checks.ts
//
// Req 3/4 (phase 05, D9/D10): the "environment" group `autopilot doctor`
// runs even with ZERO missions on disk (`claude` binary, login, `git` +
// `python3` binaries, pack workflows, config parse, Mattermost, filtered env,
// GitLab PAT) — separate from autopilot-doctor.ts's per-MISSION checks
// (settings/hooks/journal), which all need a resolved mission workspace/
// control root.
//
// Also owns `formatRunBannerLines` (D8/H7/D10, C4/C8): the SAME pure
// formatter `run`'s transport factory calls via `onBanner` and this
// module's own Mattermost/env checks render from — one literal string, one
// place, so the banner and the doctor report never drift apart (DRY).
import {
  type SettingSourcesDoctorInput,
  settingSourcesDoctorChecks,
} from "./autopilot-doctor-setting-sources-checks.js";
import type { MattermostDisabledReason } from "./mattermost-transport-preconditions.js";

export interface DoctorCheck {
  name: string;
  status: "ok" | "warn" | "fail";
  detail: string;
}

export type MattermostBannerState =
  | { enabled: true; channelId: string }
  | { enabled: false; reason: MattermostDisabledReason };

export interface FormatRunBannerLinesInput {
  mattermost: MattermostBannerState;
  /** Parent env to scan for filtered-key names — never their values. */
  env: NodeJS.ProcessEnv;
}

const FILTERED_ENV_PREFIXES = ["ANTHROPIC_", "CLAUDE_CODE_USE_"];

/** Names (never values) of parent-env keys child-env-allowlist-builder.ts filters before spawning `claude` (D10, R-19: "Model đi qua Claude Code subscription"). */
export function listFilteredEnvKeyNames(env: NodeJS.ProcessEnv): string[] {
  return Object.keys(env)
    .filter((key) => FILTERED_ENV_PREFIXES.some((prefix) => key.startsWith(prefix)))
    .sort();
}

/**
 * Banner lines printed at the start of `run`'s dispatch (Req 2, C4): exactly
 * 1 Mattermost line (D8 literal when enabled — H7 — or a named reason when
 * not) plus, only when the parent env actually has a filtered key, exactly
 * 1 warning line naming those keys (never their values).
 */
export function formatRunBannerLines(input: FormatRunBannerLinesInput): string[] {
  const lines: string[] = [
    input.mattermost.enabled
      ? `Mattermost: bật — kênh ${input.mattermost.channelId} (từ .mattermost/credentials.json)`
      : `Mattermost: tắt — ${input.mattermost.reason}`,
  ];

  const filtered = listFilteredEnvKeyNames(input.env);
  if (filtered.length > 0) {
    lines.push(
      `Cảnh báo: biến môi trường ${filtered.join(", ")} bị lọc — model chạy qua Claude Code subscription, không dùng API key.`,
    );
  }

  return lines;
}

export interface RunEnvironmentChecksDeps {
  probeClaudeVersion: () => Promise<string>;
  /**
   * D-03 (re-review backlog 2026-09-26): `<binary> --version` for `git` and
   * `python3` — `(unavailable)` when the binary can't be spawned. Same seam
   * as probeClaudeVersion (claude-version-probe.ts's probeBinaryVersion).
   */
  probeBinaryVersion: (binary: "git" | "python3") => Promise<string>;
  /**
   * Which binaries the environment group probes — default both. The
   * with-mission doctor path passes `["git"]`: the mission-level list already
   * carries "Hook interpreter (python3)" (autopilot-doctor.ts), and one root
   * cause must not print two lines (review delta 2026-09-27).
   */
  binariesToProbe?: readonly ("git" | "python3")[];
  /** `claude auth status` — never spawns a model (D9). */
  checkClaudeCodeSubscriptionAuth: () => "authenticated" | "not-authenticated";
  /** Pack has `workflows/autopilot/` — reused from init preflight, not reimplemented. */
  packHasAutopilotWorkflows: () => Promise<boolean>;
  /** Undefined config => `config_invalid` fail (Req 3). */
  effectiveConfig: { ok: true } | { ok: false; message: string };
  /** D7 (R-18): §18 keys the user's file declared that v1 never reads — [] or undefined => no `config-unused-keys` check added (Req 3). */
  unusedConfigKeyWarnings?: string[];
  mattermost: MattermostBannerState;
  env: NodeJS.ProcessEnv;
  /** Only run when the mission's effective-config has `release` configured — undefined => check skipped entirely (Req 3 "GitLab PAT khi config có release"). */
  checkGitlabPat?: () => Promise<{ found: boolean }>;
  /** 06g R4: `claude --help` text + parsed `~/.claude/settings.json` — undefined ⇒ checks skipped (unit tests predating R4). */
  settingSources?: () => Promise<SettingSourcesDoctorInput>;
}

/**
 * Environment-group checks (Req 3): runnable with 0 missions on disk. Each
 * check degrades to `warn` (never `fail`) unless it is the one condition
 * that should actively block `run` — config parse failure and a missing
 * `claude` binary/login are the only `fail`s here; Mattermost/env/GitLab
 * PAT are informational (Req 4: any `fail` anywhere sets `blocksRun`, so
 * only true blockers may use it).
 */
export async function runEnvironmentChecks(deps: RunEnvironmentChecksDeps): Promise<DoctorCheck[]> {
  const checks: DoctorCheck[] = [];

  if (!deps.effectiveConfig.ok) {
    checks.push({
      name: "config",
      status: "fail",
      detail: `config_invalid — ${deps.effectiveConfig.message}`,
    });
  } else {
    checks.push({ name: "config", status: "ok", detail: "config parse hợp lệ" });
  }

  const version = await deps.probeClaudeVersion();
  checks.push({
    name: "claude",
    status: version === "(unavailable)" ? "fail" : "ok",
    detail: version === "(unavailable)" ? "không gọi được 'claude --version'" : version,
  });

  const authState = deps.checkClaudeCodeSubscriptionAuth();
  checks.push({
    name: "claude auth",
    status: authState === "authenticated" ? "ok" : "fail",
    detail: authState === "authenticated" ? "đã đăng nhập" : "chưa đăng nhập ('claude auth login')",
  });

  // D-03 (re-review backlog 2026-09-26): both are TRUE blockers, hence `fail`
  // — the mission worktree/commits need `git`, and every pack hook is spawned
  // as `python3 -B …` (mission-workspace-hooks.ts): a missing interpreter
  // makes each hook exit 127, which Claude Code treats as a NON-blocking hook
  // error, i.e. the guards (scout-block, dangerous-command…) silently never
  // run. "Hook commands exist" only checks the script files, not the
  // interpreter.
  for (const binary of deps.binariesToProbe ?? (["git", "python3"] as const)) {
    const binaryVersion = await deps.probeBinaryVersion(binary);
    checks.push({
      name: binary,
      status: binaryVersion === "(unavailable)" ? "fail" : "ok",
      detail:
        binaryVersion === "(unavailable)"
          ? `không gọi được '${binary} --version' — cài ${binary} và đưa vào PATH`
          : binaryVersion,
    });
  }

  const hasWorkflows = await deps.packHasAutopilotWorkflows();
  checks.push({
    name: "pack workflows/autopilot",
    status: hasWorkflows ? "ok" : "fail",
    detail: hasWorkflows ? "có workflows/autopilot/" : "thiếu workflows/autopilot/ trong pack",
  });

  const bannerLines = formatRunBannerLines({ mattermost: deps.mattermost, env: deps.env });
  checks.push({
    name: "mattermost",
    status: deps.mattermost.enabled ? "ok" : "warn",
    detail: bannerLines[0] ?? "",
  });
  if (bannerLines.length > 1) {
    checks.push({ name: "env filtered", status: "warn", detail: bannerLines[1] ?? "" });
  }

  // D7 (R-18): informational only — never blocks `run` (Req 4 reserves
  // `fail` for true blockers), so `warn` even with keys present.
  if (deps.unusedConfigKeyWarnings && deps.unusedConfigKeyWarnings.length > 0) {
    checks.push({
      name: "config-unused-keys",
      status: "warn",
      detail: deps.unusedConfigKeyWarnings.join("; "),
    });
  }

  if (deps.settingSources) checks.push(...settingSourcesDoctorChecks(await deps.settingSources()));

  if (deps.checkGitlabPat) {
    const pat = await deps.checkGitlabPat();
    checks.push({
      name: "gitlab pat",
      status: pat.found ? "ok" : "warn",
      detail: pat.found
        ? "PAT có trong git credential helper"
        : "chưa lưu PAT cho host GitLab release",
    });
  }

  return checks;
}

/** Req 4: any `fail` check blocks `run`. */
export function environmentChecksBlockRun(checks: DoctorCheck[]): boolean {
  return checks.some((c) => c.status === "fail");
}
